import { Link } from "react-router-dom";
import {
    ArrowRight,
    Phone,
    Star,
    BadgeCheck,
    ShieldCheck,
    Award,
    Search,
    Fence,
    Grid3x3,
    Layers,
    Construction,
    Trees,
    Home as HomeIcon,
    BrickWall,
    MapPin,
    ExternalLink,
} from "lucide-react";
import { Section } from "@/components/ui/section";
import { Container } from "@/components/ui/container";
import { BrandButton } from "@/components/ui/brand-button";
import Seo from "@/components/Seo";
import { COMPANY } from "@/lib/constants";
import {
    PAGE_META,
    EXTERNAL_PROFILES,
    AGGREGATE_RATING,
    REVIEW_SOURCES,
} from "@/lib/seo-constants";
import { localBusinessSchema } from "@/lib/jsonld";
import { usePageContentOverride } from "@/hooks/usePageContentOverrides";
import { PROJECT_PHOTOS } from "@/data/photos";
import { TESTIMONIALS, FEATURED_TESTIMONIALS } from "@/data/testimonials";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Content data — real Reformity photos via /data/photos.js manifest
// ---------------------------------------------------------------------------

const HERO_IMG =
    PROJECT_PHOTOS.interlocking?.[0]?.src ||
    PROJECT_PHOTOS.fencing?.[0]?.src ||
    "/projects/fencing/aluminum-fence-img-20250502-wa0056.webp";

// Trust bar: per-platform verified counts + ratings.
const PLATFORM_ICONS = {
    Homestars: Award,
    Bark: ShieldCheck,
    Houzz: Star,
    BBB: BadgeCheck,
    Google: Search,
};

const PLATFORM_ORDER = ["Homestars", "Bark", "Houzz", "BBB", "Google"];

const formatPlatformMeta = (meta) => {
    if (meta.accreditation) {
        return `Accredited · ${meta.accreditation} · ${meta.count} reviews`;
    }
    const noun = meta.count === 1 ? "review" : "reviews";
    return `${meta.count} ${noun} · ${meta.rating.toFixed(1)}★`;
};

const SERVICE_CARDS = [
    {
        icon: Fence,
        title: "Fencing",
        desc: "Wood, vinyl, aluminum, and chain-link fences built to last GTA winters.",
        path: "/services/fencing",
    },
    {
        icon: Grid3x3,
        title: "Interlocking & Paving",
        desc: "Driveways, walkways, and patios that elevate your curb appeal.",
        path: "/services/interlocking",
    },
    {
        icon: Layers,
        title: "Decking",
        desc: "Custom composite and pressure-treated decks designed for outdoor living.",
        path: "/services/decking",
    },
    {
        icon: Construction,
        title: "Concrete & Asphalt",
        desc: "Driveways, walkways, and structural slabs poured to spec.",
        path: "/services/concrete",
    },
    {
        icon: Trees,
        title: "Landscaping",
        desc: "Sod, garden beds, retaining walls, and full backyard transformations.",
        path: "/services/landscaping",
    },
    {
        icon: HomeIcon,
        title: "Interior Renovations",
        desc: "Kitchens, bathrooms, basements, and epoxy floors finished to impress.",
        path: "/services/renovations",
    },
    {
        icon: BrickWall,
        title: "Masonry",
        desc: "Brickwork, tuckpointing, parging, foundation repair, and underpinning.",
        path: "/services/masonry",
    },
];

const pickFirst = (slug) => PROJECT_PHOTOS[slug]?.[0];
const PROJECTS = [
    {
        img: pickFirst("fencing")?.src,
        title: "Cedar & Aluminum Fence",
        location: "Brampton",
        tag: "Fencing",
        category: "fencing",
    },
    {
        img: pickFirst("interlocking")?.src,
        title: "Interlocking Driveway",
        location: "Mississauga",
        tag: "Interlocking",
        category: "interlocking",
    },
    {
        img: pickFirst("decking")?.src,
        title: "Backyard Deck Build",
        location: "Vaughan",
        tag: "Decking",
        category: "decking",
    },
    {
        img: pickFirst("concrete")?.src,
        title: "Concrete & Asphalt Drive",
        location: "Oakville",
        tag: "Concrete",
        category: "concrete",
    },
    {
        img: pickFirst("landscaping")?.src,
        title: "Custom Landscape Build",
        location: "Burlington",
        tag: "Landscaping",
        category: "landscaping",
    },
    {
        img: pickFirst("renovations")?.src,
        title: "Interior Renovation",
        location: "Toronto",
        tag: "Interior",
        category: "renovations",
    },
];

const PROCESS = [
    {
        num: "01",
        title: "Free On-Site Quote",
        body: "We measure, listen, and price your project transparently.",
    },
    {
        num: "02",
        title: "Detailed Proposal",
        body: "Materials, timeline, and a fixed quote. No surprises.",
    },
    {
        num: "03",
        title: "Pro Crew Build",
        body: "Licensed, insured, in-house crews. No flaky subs.",
    },
    {
        num: "04",
        title: "Final Walk-Through",
        body: "We walk every detail with you before we call it done.",
    },
];

const SERVICE_CITIES = [
    "Brampton",
    "Mississauga",
    "Toronto",
    "Vaughan",
    "Oakville",
    "Burlington",
    "Milton",
    "Caledon",
    "Hamilton",
];

// ---------------------------------------------------------------------------
// Section primitives
// ---------------------------------------------------------------------------

const Eyebrow = ({ tone = "coral", children }) => (
    <p
        className={cn(
            "text-xs sm:text-sm uppercase tracking-[0.25em] font-semibold mb-3",
            tone === "coral" ? "text-brand-coral" : "text-brand-cyan",
        )}
    >
        {children}
    </p>
);

const SectionHeading = ({ children, light = false }) => (
    <h2
        className={cn(
            "font-display text-4xl sm:text-5xl lg:text-6xl tracking-wide leading-none",
            light ? "text-white" : "text-brand-dark",
        )}
    >
        {children}
    </h2>
);

const StarRow = () => (
    <div className="flex items-center gap-1">
        {Array.from({ length: 5 }).map((_, i) => (
            <Star
                key={i}
                className="h-4 w-4 text-brand-cyan fill-brand-cyan"
                strokeWidth={1.5}
            />
        ))}
    </div>
);

// ---------------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------------

const Hero = ({ image, alt }) => (
    <section
        className="relative w-full overflow-hidden bg-brand-dark text-white min-h-[70vh] md:min-h-[85vh] flex items-center"
        data-testid="home-hero"
    >
        <div className="absolute inset-0">
            <img
                src={image}
                alt={alt}
                className="h-full w-full object-cover"
                loading="eager"
            />
            <div className="absolute inset-0 bg-brand-dark/65" aria-hidden="true" />
            <div
                className="absolute inset-0 bg-gradient-to-r from-brand-dark/80 via-brand-dark/30 to-transparent"
                aria-hidden="true"
            />
        </div>

        <Container className="relative z-10 py-24 md:py-28">
            <div className="max-w-4xl">
                <p
                    className="text-xs sm:text-sm uppercase tracking-[0.3em] text-brand-cyan font-semibold mb-5 animate-fade-in-up"
                    data-testid="home-hero-eyebrow"
                >
                    Exterior &amp; Interior Contracting
                </p>
                <h1
                    className="font-display text-5xl sm:text-7xl lg:text-8xl xl:text-9xl leading-[0.92] tracking-wide text-white animate-fade-in-up"
                    style={{ animationDelay: "60ms" }}
                    aria-label="Built to last. Finished to impress."
                    data-testid="home-hero-title"
                >
                    Built to last.
                    <br />
                    <span className="text-brand-coral">Finished to impress.</span>
                </h1>
                <p
                    className="mt-6 max-w-2xl text-base sm:text-lg md:text-xl text-white/85 font-light italic leading-relaxed animate-fade-in-up"
                    style={{ animationDelay: "120ms" }}
                >
                    Premium fencing, interlocking, decking, concrete, landscaping, and
                    interior renovations across the Greater Toronto Area.
                </p>

                <div
                    className="mt-9 flex flex-col sm:flex-row gap-3 sm:gap-4 animate-fade-in-up"
                    style={{ animationDelay: "180ms" }}
                >
                    <BrandButton asChild variant="primary" size="lg">
                        <Link to="/contact" data-testid="home-hero-cta-primary">
                            Get a Free Quote
                            <ArrowRight className="h-4 w-4" strokeWidth={2.25} />
                        </Link>
                    </BrandButton>
                    <BrandButton asChild variant="outline" tone="onDark" size="lg">
                        <a
                            href={COMPANY.phoneHref}
                            data-testid="home-hero-cta-secondary"
                        >
                            <Phone className="h-4 w-4" strokeWidth={2.25} />
                            Call {COMPANY.phone}
                        </a>
                    </BrandButton>
                </div>

                <p
                    className="mt-10 text-xs sm:text-sm text-white/70 max-w-2xl animate-fade-in-up"
                    style={{ animationDelay: "240ms" }}
                    data-testid="home-hero-trust"
                >
                    <span className="text-brand-cyan font-semibold">
                        {AGGREGATE_RATING.average.toFixed(1)}★ across{" "}
                        {AGGREGATE_RATING.total} verified reviews
                    </span>
                    <span className="mx-2 text-white/30">•</span>
                    <a
                        href={EXTERNAL_PROFILES.bbb}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="hover:underline"
                    >
                        BBB Accredited A+
                    </a>
                    <span className="mx-2 text-white/30">•</span>
                    WSIB Covered
                    <span className="mx-2 text-white/30">•</span>
                    $5M Liability Insurance
                </p>
            </div>
        </Container>
    </section>
);

const TrustBar = () => (
    <Section className="py-10 md:py-14" data-testid="home-trustbar">
        <Container>
            <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-y-7 gap-x-4 md:gap-x-6 items-start">
                {PLATFORM_ORDER.map((name) => {
                    const meta = REVIEW_SOURCES[name];
                    const Icon = PLATFORM_ICONS[name] || ShieldCheck;
                    return (
                        <li key={name} className="text-center">
                            <a
                                href={meta.profileUrl}
                                target="_blank"
                                rel="noopener noreferrer nofollow"
                                className="group inline-flex flex-col items-center gap-1.5 text-brand-dark hover:text-brand-cyan transition-colors duration-200"
                                data-testid={`home-trust-platform-${name.toLowerCase()}`}
                            >
                                <span className="flex items-center gap-2 text-xs sm:text-sm uppercase tracking-wider font-semibold">
                                    <Icon
                                        className="h-4 w-4 sm:h-5 sm:w-5 text-brand-coral"
                                        strokeWidth={2}
                                    />
                                    {name}
                                </span>
                                <span className="text-[11px] sm:text-xs text-brand-gray font-medium tracking-wide">
                                    {formatPlatformMeta(meta)}
                                </span>
                            </a>
                        </li>
                    );
                })}
            </ul>
            <p
                className="mt-8 text-center text-[11px] sm:text-xs uppercase tracking-[0.2em] text-brand-gray/80 font-semibold"
                data-testid="home-trust-license"
            >
                <ShieldCheck
                    className="inline h-4 w-4 mr-1.5 text-brand-cyan -mt-0.5"
                    strokeWidth={2.25}
                />
                Licensed &amp; Insured · WSIB Covered · $5M Liability
            </p>
        </Container>
    </Section>
);

const Services = () => (
    <Section dark data-testid="home-services">
        <Container>
            <div className="mb-12 md:mb-16 max-w-2xl">
                <Eyebrow tone="cyan">What We Build</Eyebrow>
                <SectionHeading light>
                    Six core trades. One trusted crew.
                </SectionHeading>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
                {SERVICE_CARDS.map(({ icon: Icon, title, desc, path }) => (
                    <Link
                        key={path}
                        to={path}
                        className="group relative bg-brand-dark p-7 md:p-8 transition-colors duration-200 hover:bg-[#333] border-t-2 border-transparent hover:border-brand-cyan"
                        data-testid={`home-service-card-${path.split("/").pop()}`}
                    >
                        <div className="flex items-start justify-between mb-6">
                            <span className="flex h-12 w-12 items-center justify-center bg-white/5 text-brand-cyan group-hover:bg-brand-cyan group-hover:text-white transition-colors">
                                <Icon className="h-6 w-6" strokeWidth={2} />
                            </span>
                            <ArrowRight
                                className="h-5 w-5 text-white/40 group-hover:text-brand-cyan group-hover:translate-x-1 transition-all"
                                strokeWidth={2.25}
                            />
                        </div>
                        <h3 className="font-display text-2xl md:text-3xl tracking-wide text-white mb-3">
                            {title}
                        </h3>
                        <p className="text-sm text-white/65 leading-relaxed">{desc}</p>
                        <span className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-brand-coral group-hover:text-brand-cyan transition-colors">
                            View Service
                        </span>
                    </Link>
                ))}
            </div>
        </Container>
    </Section>
);

const Projects = () => (
    <Section data-testid="home-projects">
        <Container>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-14">
                <div className="max-w-xl">
                    <Eyebrow>Recent Work</Eyebrow>
                    <SectionHeading>A few projects we&apos;re proud of.</SectionHeading>
                </div>
                <Link
                    to="/gallery"
                    className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-brand-dark hover:text-brand-cyan transition-colors"
                    data-testid="home-projects-viewall"
                >
                    View Full Gallery
                    <ArrowRight className="h-4 w-4" strokeWidth={2.25} />
                </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {PROJECTS.map((p) => (
                    <Link
                        key={p.title}
                        to="/gallery"
                        className="group relative block overflow-hidden bg-brand-dark aspect-[4/3]"
                        data-testid={`home-project-${p.tag.toLowerCase()}`}
                    >
                        <img
                            src={p.img}
                            alt={`${p.title} in ${p.location}`}
                            loading="lazy"
                            className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                        <div
                            className="absolute inset-0 bg-gradient-to-t from-brand-dark via-brand-dark/30 to-transparent opacity-90 transition-opacity duration-300 group-hover:opacity-100"
                            aria-hidden="true"
                        />
                        <div className="absolute inset-x-0 bottom-0 p-5">
                            <span className="inline-block bg-brand-coral text-white text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1 mb-2">
                                {p.tag}
                            </span>
                            <h3 className="font-display text-xl sm:text-2xl tracking-wide text-white leading-tight">
                                {p.title}
                            </h3>
                            <p className="text-xs text-white/70 mt-1 flex items-center gap-1.5">
                                <MapPin className="h-3 w-3" strokeWidth={2.5} />
                                {p.location}
                            </p>
                        </div>
                    </Link>
                ))}
            </div>
        </Container>
    </Section>
);

const Process = () => (
    <Section dark data-testid="home-process">
        <Container>
            <div className="mb-12 md:mb-16 max-w-2xl">
                <Eyebrow tone="cyan">How It Works</Eyebrow>
                <SectionHeading light>From quote to clean-up.</SectionHeading>
                <p className="mt-5 text-base text-white/70 leading-relaxed max-w-xl">
                    Every Reformity project follows the same four-step build process —
                    so you always know what&apos;s next and what to expect.
                </p>
            </div>

            <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
                {PROCESS.map((p) => (
                    <li
                        key={p.num}
                        className="relative border-t-2 border-brand-cyan/30 pt-6"
                        data-testid={`home-process-step-${p.num}`}
                    >
                        <span className="font-display text-5xl md:text-6xl text-brand-cyan leading-none block">
                            {p.num}
                        </span>
                        <h3 className="mt-4 font-sans font-bold text-lg uppercase tracking-wider text-white">
                            {p.title}
                        </h3>
                        <p className="mt-2 text-sm text-white/70 leading-relaxed">
                            {p.body}
                        </p>
                    </li>
                ))}
            </ol>
        </Container>
    </Section>
);

const Testimonials = () => (
    <Section data-testid="home-testimonials">
        <Container>
            <div className="mb-12 md:mb-14 max-w-2xl">
                <Eyebrow>What Homeowners Say</Eyebrow>
                <SectionHeading>Real reviews. Real homes.</SectionHeading>
                <p className="mt-5 text-base text-brand-dark/75 leading-relaxed max-w-xl">
                    <span className="font-semibold text-brand-dark">
                        {AGGREGATE_RATING.average.toFixed(1)}★
                    </span>{" "}
                    average across{" "}
                    <span className="font-semibold text-brand-dark">
                        {AGGREGATE_RATING.total} verified reviews
                    </span>{" "}
                    on Homestars, Bark, Houzz, BBB, and Google.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {FEATURED_TESTIMONIALS.map((t, i) => {
                    const source = REVIEW_SOURCES[t.source] || {};
                    return (
                        <figure
                            key={t.id}
                            className="bg-white border border-brand-dark/10 p-6 md:p-7 flex flex-col h-full hover:border-brand-cyan transition-colors"
                            data-testid={`home-testimonial-${i}`}
                        >
                            <div className="flex items-center justify-between mb-4">
                                <StarRow />
                                {t.verified && (
                                    <span
                                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-brand-cyan bg-brand-cyan/10 px-2 py-1"
                                        data-testid={`home-testimonial-${i}-verified`}
                                    >
                                        <BadgeCheck
                                            className="h-3 w-3"
                                            strokeWidth={2.5}
                                        />
                                        Verified
                                    </span>
                                )}
                            </div>
                            <blockquote className="font-light italic text-base text-brand-dark/85 leading-relaxed flex-1">
                                {`"${t.quote}"`}
                            </blockquote>
                            <figcaption className="mt-5 pt-5 border-t border-brand-dark/10 flex items-end justify-between gap-3">
                                <div>
                                    <p className="font-semibold text-sm text-brand-dark">
                                        {t.name}
                                    </p>
                                    <p className="text-xs text-brand-gray">
                                        via {t.source}
                                    </p>
                                </div>
                                <a
                                    href={source.profileUrl}
                                    target="_blank"
                                    rel="noopener noreferrer nofollow"
                                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-brand-coral hover:text-brand-cyan transition-colors whitespace-nowrap"
                                    data-testid={`home-testimonial-${i}-source-link`}
                                >
                                    View on {t.source}
                                    <ExternalLink
                                        className="h-3 w-3"
                                        strokeWidth={2.5}
                                    />
                                </a>
                            </figcaption>
                        </figure>
                    );
                })}
            </div>

            <div
                className="mt-10 md:mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-t border-brand-dark/10 pt-8"
                data-testid="home-testimonials-footer"
            >
                <p className="text-sm text-brand-dark/70 max-w-md">
                    All reviews are sourced directly from verified third-party
                    platforms — no edits, no curation.
                </p>
                <Link
                    to="/reviews"
                    className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-brand-dark hover:text-brand-cyan transition-colors whitespace-nowrap"
                    data-testid="home-testimonials-viewall"
                >
                    Read All {TESTIMONIALS.length} Verified Reviews
                    <ArrowRight className="h-4 w-4" strokeWidth={2.25} />
                </Link>
            </div>
        </Container>
    </Section>
);

const CtaBand = () => (
    <Section dark className="py-16 md:py-24" data-testid="home-cta-band">
        <Container>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                <div className="lg:col-span-7">
                    <Eyebrow tone="cyan">Ready to Build?</Eyebrow>
                    <SectionHeading light>
                        Get a free, no-obligation quote in under 24 hours.
                    </SectionHeading>
                    <p className="mt-5 text-base text-white/70 max-w-xl">
                        Tell us about your project — fencing, interlocking, decking,
                        concrete, landscaping, or a full interior reno — and we&apos;ll be in
                        touch within one business day.
                    </p>
                </div>
                <div className="lg:col-span-5 flex flex-col gap-3 lg:items-end">
                    <BrandButton
                        asChild
                        variant="primary"
                        size="lg"
                        className="w-full lg:w-auto"
                    >
                        <Link to="/contact" data-testid="home-cta-quote">
                            Start My Free Quote
                            <ArrowRight className="h-4 w-4" strokeWidth={2.25} />
                        </Link>
                    </BrandButton>
                    <BrandButton
                        asChild
                        variant="outline"
                        tone="onDark"
                        size="lg"
                        className="w-full lg:w-auto"
                    >
                        <a href={COMPANY.phoneHref} data-testid="home-cta-call">
                            <Phone className="h-4 w-4" strokeWidth={2.25} />
                            Call {COMPANY.phone}
                        </a>
                    </BrandButton>
                </div>
            </div>
        </Container>
    </Section>
);

const ServiceAreaTeaser = () => (
    <Section className="py-12 md:py-16" data-testid="home-servicearea">
        <Container>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 border-l-2 border-brand-coral pl-6 md:pl-8">
                <div className="max-w-2xl">
                    <p className="text-xs uppercase tracking-[0.25em] text-brand-coral font-semibold mb-2">
                        Service Area
                    </p>
                    <h3 className="font-display text-2xl sm:text-3xl tracking-wide text-brand-dark mb-3">
                        Proudly serving the GTA &amp; Golden Horseshoe.
                    </h3>
                    <p className="text-sm text-brand-dark/70">
                        {SERVICE_CITIES.join(" • ")} — and more.
                    </p>
                </div>
                <Link
                    to="/service-area"
                    className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-brand-dark hover:text-brand-cyan transition-colors whitespace-nowrap"
                    data-testid="home-servicearea-link"
                >
                    See Full Map
                    <ArrowRight className="h-4 w-4" strokeWidth={2.25} />
                </Link>
            </div>
        </Container>
    </Section>
);

const Home = () => {
    const override = usePageContentOverride("track-home:residential");
    const heroImage = override?.hero_image || HERO_IMG;
    const heroAlt =
        override?.hero_image_alt ||
        "Modern Brampton home exterior at twilight — built by Reformity Inc.";
    const meta = {
        title: override?.seo_title || PAGE_META.home.title,
        description: override?.seo_description || PAGE_META.home.description,
    };
    return (
    <>
        <Seo
            {...meta}
            path="/residential"
            image={heroImage}
            jsonLd={[localBusinessSchema()]}
        />
        <Hero image={heroImage} alt={heroAlt} />
        <TrustBar />
        <Services />
        <Projects />
        <Process />
        <Testimonials />
        <CtaBand />
        <ServiceAreaTeaser />
    </>
    );
};

export default Home;
